import React, { useEffect, useState, useContext } from 'react';
import api, { getUserFacingErrorMessage } from '../utils/api';
import { track } from '../utils/analytics';
import { UserContext } from '../App';
import { useLocation, useNavigate } from 'react-router-dom';
import NomineeRequiredBanner from './NomineeRequiredBanner';

export default function UploadSignedWill({ nomineeGate = false }) {
  const { currentUser } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [file, setFile] = useState(null);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const res = await api.get('/will/list');
    setCurrent(res.data.currentFile || null);
  };

  useEffect(() => { refresh(); }, []);

  const guessContentType = (f) => {
    const name = String(f?.name || '');
    const ext = (name.split('.').pop() || '').toLowerCase();
    if (ext === 'pdf') return 'application/pdf';
    if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    if (ext === 'png') return 'image/png';
    if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
    return (f && f.type) ? f.type : 'application/octet-stream';
  };

  const submit = async () => {
    if (nomineeGate) {
      navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
      return;
    }
    if (!file) return;
    try {
      setBusy(true);
      track('will_upload_started', { userId: currentUser?.id, ext: (file.name.split('.').pop()||'').toLowerCase(), size: file.size });
      const contentType = guessContentType(file);

      // 1) Init upload (get presigned POST)
      const init = await api.post('/will/upload/init', {
        fileName: file.name,
        contentType,
        bytesExpected: file.size,
      });
      const { objectId, upload } = init.data || {};
      if (!objectId || !upload?.url || !upload?.fields) throw new Error('Upload init failed');

      // 2) Upload directly to S3
      const formData = new FormData();
      Object.entries(upload.fields).forEach(([k, v]) => formData.append(k, v));
      formData.append('file', file);
      const s3Resp = await fetch(upload.url, { method: 'POST', body: formData });
      if (!s3Resp.ok) {
        throw new Error(`S3 upload failed (${s3Resp.status})`);
      }

      // 3) Finalize
      await api.post('/will/upload/finalize', { objectId });

      await refresh();
      track('will_upload_succeeded', { userId: currentUser?.id, ext: (file.name.split('.').pop()||'').toLowerCase(), size: file.size });
      setFile(null);
      alert('Upload successful.');
    } catch (err) {
      console.error(err);
      track('will_upload_failed', { userId: currentUser?.id, errorCode: String(err?.response?.status || '500') });
      const code = err?.response?.data?.code;
      if (err?.response?.status === 409 && code === 'NOMINEE_REQUIRED') {
        alert(getUserFacingErrorMessage(err, {
          fallback: 'Add at least one nominee before you can upload a will.',
          includeSupportForUnknown: false,
        }));
        navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
      } else {
        alert(getUserFacingErrorMessage(err, {
          fallback: 'We could not upload your will. Check the file and try again.',
          includeSupportForUnknown: false,
        }));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {nomineeGate && <NomineeRequiredBanner returnTo={location.pathname} />}
      {current && (
        <div style={{ background: '#fff', border: '1px solid var(--mist-300)', borderRadius: 12, padding: 12 }}>
          <div style={{ fontWeight: 600 }}>Current file</div>
          <div style={{ fontSize: 14 }}>{current.fileName} — {new Date(current.date).toLocaleString()}</div>
        </div>
      )}
      <div style={{ background: '#fff', border: '1px solid var(--mist-300)', borderRadius: 12, padding: 16 }}>
        <div style={{ display: 'grid', gap: 8 }}>
          <label>Choose file (PDF, JPG, PNG, DOCX; max 20MB)
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={e=>setFile(e.target.files?.[0] || null)} aria-label="Signed will file" disabled={nomineeGate}/>
          </label>
          <button
            type="button"
            onClick={submit}
            disabled={!file || busy || nomineeGate}
            style={{ padding: '10px 14px', border: 'none', borderRadius: 10, background: nomineeGate ? '#8fa8a0' : 'var(--sage-600)', color: '#fff', fontWeight: 700 }}
            title={nomineeGate ? 'Add a nominee first' : 'Upload'}
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
}


